import React, { useState, useRef, useCallback } from "react"
import { Bot, X } from "lucide-react"

const NOTE_MIN_HEIGHT = 80
const NOTE_MAX_HEIGHT = 320

const ACTIONS = [
  { value: "tone",                 label: "Improve tone" },
  { value: "translate",            label: "Translate" },
  { value: "analyze_incoming",     label: "Analyze message" },
  { value: "summary",              label: "Chat summary" },
  { value: "process_notification", label: "Parse notification" },
  { value: "flight_info",          label: "Flight info" },
]

const TONES = [
  { value: "business_standard", label: "Business standard" },
  { value: "soft",              label: "Soft / polite" },
  { value: "urgent",            label: "Urgent" },
  { value: "short",             label: "Short" },
  { value: "detailed",          label: "Detailed" },
  { value: "apologetic",        label: "Apologetic" },
  { value: "neutral",           label: "Neutral" },
  { value: "premium",           label: "Premium" },
]

const LANGUAGES = [
  { value: "ua", label: "Ukrainian" },
  { value: "en", label: "English" },
]

const TRANSLATE_DIRS = [
  { value: "UA_EN", label: "UA → EN" },
  { value: "EN_UA", label: "EN → UA" },
]

const getCsrf = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""

const sendFeedback = (actionType, rawInput, finalText, language, tone) => {
  fetch("/book/ai/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrf() },
    body: JSON.stringify({ action_type: actionType, raw_input: rawInput, final_text: finalText, language, tone }),
  }).catch(() => {})
}

const AiAssistPanel = ({ bundleId, onInsert }) => {
  const [open, setOpen]       = useState(false)
  const [action, setAction]   = useState("tone")
  const [tone, setTone]       = useState("business_standard")
  const [language, setLang]   = useState("ua")
  const [transDir, setDir]    = useState("UA_EN")
  const [input, setInput]     = useState("")
  const [result, setResult]   = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState("")
  const noteRef = useRef(null)

  const growNote = useCallback(() => {
    const el = noteRef.current
    if (!el || el.scrollHeight <= el.clientHeight) return
    el.style.height = `${Math.min(NOTE_MAX_HEIGHT, el.scrollHeight)}px`
  }, [])

  const run = async () => {
    setLoading(true)
    setError("")
    setResult("")
    try {
      const res = await fetch("/book/ai/assist", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrf() },
        body: JSON.stringify({
          action_type:          action,
          text:                 input,
          tone,
          language,
          translation_direction: transDir,
          bundle_id:            bundleId,
        }),
      })
      const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }))
      if (data.error) setError(data.error)
      else if (typeof data.result === "object" && data.result !== null) {
        const draft = data.result.recommended_agent_reply ?? data.result.suggested_agent_message
        setResult(draft ?? JSON.stringify(data.result, null, 2))
      } else {
        setResult(data.result)
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        type="button"
        title="AI Assist"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg transition-colors min-h-[44px] ${
          open
            ? "bg-violet-100 text-violet-700"
            : "hover:bg-slate-100 active:bg-slate-200 text-slate-500 hover:text-slate-700"
        }`}
      >
        <Bot className="w-4 h-4" />
        <span className="text-sm">AI Assist</span>
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 z-30 bg-white border border-slate-200 rounded-xl shadow-xl p-3 mb-1 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
              <Bot className="w-3.5 h-3.5 text-violet-500" /> AI Assist
            </span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value)
                setResult("")
              }}
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
            >
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>

            {action === "tone" && (
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
              >
                {TONES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            )}

            {action === "translate" ? (
              <select
                value={transDir}
                onChange={(e) => setDir(e.target.value)}
                className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
              >
                {TRANSLATE_DIRS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            ) : (
              <select
                value={language}
                onChange={(e) => setLang(e.target.value)}
                className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            )}
          </div>

          <textarea
            ref={noteRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); growNote() }}
            rows={2}
            placeholder="Short note (leave empty for summary / analysis)"
            style={{ minHeight: NOTE_MIN_HEIGHT, maxHeight: NOTE_MAX_HEIGHT }}
            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 resize-y focus:outline-none focus:ring-1 focus:ring-violet-400"
          />

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={run}
              disabled={loading}
              className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs rounded-lg disabled:opacity-50 transition-colors"
            >
              {loading ? "Thinking…" : "Generate"}
            </button>
            {result && (
              <button
                type="button"
                onClick={() => {
                  sendFeedback(action, input, result, language, tone)
                  onInsert(result)
                  setOpen(false)
                }}
                className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors"
              >
                Insert
              </button>
            )}
            {input && !result && !loading && (
              <button
                type="button"
                onClick={() => { onInsert(input); setOpen(false) }}
                className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs rounded-lg transition-colors"
              >
                Insert as-is
              </button>
            )}
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          {result && (
            <textarea
              value={result}
              onChange={(e) => setResult(e.target.value)}
              rows={4}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none bg-slate-50 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          )}
        </div>
      )}
    </>
  )
}

export default AiAssistPanel
